/**
 * パスワードなしログイン（メールでログイン）の表示ルール。
 *
 * フレームワークに依存しない純粋な関数だけを置く（単体で確認できるように）。
 * 判定の正はバックエンド（GET /auth/policy）で、画面はそれを描くだけ。
 * バックエンドは画面の表示に関係なく、終了した方式を自分で止める。
 */

export interface AuthPolicy {
    passwordLoginAllowed: boolean;
    /** ISO-8601（オフセット付き）。予定がなければ null。 */
    passwordLoginRetiresAt: string | null;
    easilyLoginAllowed: boolean;
    easilyLoginRetiresAt: string | null;
    magicLinkAvailable: boolean;
    passkeyAvailable: boolean;
    passwordSignupAllowed: boolean;
    passwordlessSignupAvailable: boolean;
    /** メールアドレスのないアカウント向けの「ユーザー名＋パスワード」入口。 */
    noEmailPasswordLoginAllowed: boolean;
    noEmailPasswordLoginRetiresAt: string | null;
}

type BoolKey =
    | "passwordLoginAllowed"
    | "easilyLoginAllowed"
    | "magicLinkAvailable"
    | "passkeyAvailable"
    | "passwordSignupAllowed"
    | "passwordlessSignupAvailable"
    | "noEmailPasswordLoginAllowed";

type InstantKey = "passwordLoginRetiresAt" | "easilyLoginRetiresAt" | "noEmailPasswordLoginRetiresAt";

/**
 * これまでの動作（パスワードでのログインのみ）。/auth/policy が 404・失敗・時間切れのときに使う。
 */
export const LEGACY_POLICY: Readonly<AuthPolicy> = Object.freeze({
    passwordLoginAllowed: true,
    passwordLoginRetiresAt: null,
    easilyLoginAllowed: true,
    easilyLoginRetiresAt: null,
    magicLinkAvailable: false,
    passkeyAvailable: false,
    passwordSignupAllowed: true,
    passwordlessSignupAvailable: false,
    noEmailPasswordLoginAllowed: false,
    noEmailPasswordLoginRetiresAt: null,
});

export function legacyPolicy(): AuthPolicy {
    return {...LEGACY_POLICY};
}

/**
 * /auth/policy の応答を厳密に読む。本物の boolean でない項目は、その項目だけ従来の値にする
 * （おかしな応答でパスワード欄が消えることがないように）。
 */
export function parseAuthPolicy(raw: unknown): AuthPolicy {
    if (!raw || typeof raw !== "object" || Array.isArray(raw)) {
        return legacyPolicy();
    }
    const r = raw as Record<string, unknown>;
    const bool = (k: BoolKey): boolean => (typeof r[k] === "boolean" ? (r[k] as boolean) : LEGACY_POLICY[k]);
    const instant = (k: InstantKey): string | null => {
        const v = r[k];
        return typeof v === "string" && !isNaN(Date.parse(v)) ? v : null;
    };

    const policy: AuthPolicy = {
        passwordLoginAllowed: bool("passwordLoginAllowed"),
        passwordLoginRetiresAt: instant("passwordLoginRetiresAt"),
        easilyLoginAllowed: bool("easilyLoginAllowed"),
        easilyLoginRetiresAt: instant("easilyLoginRetiresAt"),
        magicLinkAvailable: bool("magicLinkAvailable"),
        passkeyAvailable: bool("passkeyAvailable"),
        passwordSignupAllowed: bool("passwordSignupAllowed"),
        passwordlessSignupAvailable: bool("passwordlessSignupAvailable"),
        noEmailPasswordLoginAllowed: bool("noEmailPasswordLoginAllowed"),
        noEmailPasswordLoginRetiresAt: instant("noEmailPasswordLoginRetiresAt"),
    };
    // ログインする手段が一つもない設定ミスのときは、パスワード欄を残す（バックエンドが自分の文言で答える）。
    if (!policy.passwordLoginAllowed && !policy.magicLinkAvailable) {
        policy.passwordLoginAllowed = true;
    }
    return policy;
}

/** ログイン画面に何を出すか。 */
export interface LoginScreen {
    /** 「ログイン用のメールを送る」を主役にする。 */
    magicLink: boolean;
    /** パスワード欄を出す。 */
    password: boolean;
    /** パスワード欄が主役（これまでの画面そのまま）。 */
    passwordPrimary: boolean;
    /** 設定されていれば「◯で、パスワードでのログインは終了します」を出す。 */
    passwordRetiresAt: string | null;
    /** 控えめな「メールアドレスが登録されていない方」リンク。 */
    noEmailEntry: boolean;
    /** 「パスワードを忘れた方」。 */
    forgotPassword: boolean;
}

export function loginScreen(policy: AuthPolicy): LoginScreen {
    const password = policy.passwordLoginAllowed;
    return {
        magicLink: policy.magicLinkAvailable,
        password,
        passwordPrimary: password && !policy.magicLinkAvailable,
        passwordRetiresAt: password ? policy.passwordLoginRetiresAt : null,
        noEmailEntry: policy.magicLinkAvailable && policy.noEmailPasswordLoginAllowed,
        forgotPassword: password,
    };
}

/**
 * 新規登録の最後の手順。パスワードでの登録が終わり、かつパスワードなし登録が使えるときだけ切り替える
 * （移行期間中はこれまでの登録画面のまま）。
 */
export function signupMode(policy: AuthPolicy): "passwordless" | "password" {
    return !policy.passwordSignupAllowed && policy.passwordlessSignupAvailable ? "passwordless" : "password";
}

/** ログイン済みでメールアドレスのない人に「メールアドレスを設定してください」を出すか。 */
export function needsMailAddressSetup(policy: AuthPolicy, user: { id?: string; email?: string | null } | null): boolean {
    if (!policy.magicLinkAvailable || !user || !user.id) {
        return false;
    }
    return !user.email || String(user.email).trim() === "";
}

/** バックエンドの MailAddresses.normalize と同じ形のチェック。 */
export function isEmailLike(value: string): boolean {
    const v = (value || "").trim();
    return v.length > 0 && v.length <= 100 && /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(v);
}

/**
 * 「2027年1月31日」。終了日は日本の暦日で告知するので、閲覧者の時間帯で日付がずれないように Asia/Tokyo で出す。
 */
export function formatRetireDate(iso: string | null | undefined): string {
    if (!iso) {
        return "";
    }
    const d = new Date(iso);
    if (isNaN(d.getTime())) {
        return "";
    }
    try {
        return new Intl.DateTimeFormat("ja-JP", {
            timeZone: "Asia/Tokyo",
            year: "numeric",
            month: "long",
            day: "numeric",
        }).format(d);
    } catch (e) {
        // Intl のタイムゾーンが使えない古いブラウザ向け: UTC+9 で計算する。
        const t = new Date(d.getTime() + 9 * 60 * 60 * 1000);
        return `${t.getUTCFullYear()}年${t.getUTCMonth() + 1}月${t.getUTCDate()}日`;
    }
}

/**
 * ログイン後の行き先として安全な、同じサイト内のパスだけを通す。
 * 「//」「/\」で始まるもの、スキーム付き、制御文字を含むものは fallback にする。
 */
export function safeRedirect(raw: string | null | undefined, fallback = "/mypage"): string {
    if (!raw || typeof raw !== "string") {
        return fallback;
    }
    if (raw.charAt(0) !== "/" || raw.startsWith("//") || raw.startsWith("/\\")) {
        return fallback;
    }
    // 制御文字（タブ・改行はブラウザが取り除くため、スキームを隠せてしまう）。
    // eslint-disable-next-line no-control-regex
    if (/[\u0000-\u001f\u007f]/.test(raw)) {
        return fallback;
    }
    return raw;
}

/** location.hash（「#token=abc」）から token を取り出す。 */
export function tokenFromHash(hash: string | null | undefined): string {
    if (!hash) {
        return "";
    }
    const body = hash.charAt(0) === "#" ? hash.slice(1) : hash;
    for (const part of body.split("&")) {
        const eq = part.indexOf("=");
        const key = eq < 0 ? part : part.slice(0, eq);
        if (key === "token") {
            const value = eq < 0 ? "" : part.slice(eq + 1);
            try {
                return decodeURIComponent(value.replace(/\+/g, " "));
            } catch (e) {
                return "";
            }
        }
    }
    return "";
}

/** 再送ボタンを押せるまでの秒数。 */
export const RESEND_COOLDOWN_SECONDS = 60;

/** /auth/policy を待つ上限。過ぎたらこれまでの画面を出す。 */
export const POLICY_TIMEOUT_MS = 4000;
